import { useEffect, useRef, useState } from 'react'
import {
  loadQuestPicks,
  loadQuestResults,
  saveQuestPicks,
  loadHasStartedGw,
  saveHasStartedGw,
} from '../storage'
import { useAutosave } from './usePersistedState'
import type { QuestResult } from '../questResults'
import type { Game, QuestId, QuestPickState } from '../types'

export interface UseQuestPicksOptions {
  currentGameweek: number
  isLoading: boolean
  /** Bumped whenever results move (live poll, dev tools, world change) — the signal that a
   *  gameweek may have become gradeable, and so the quest results worth re-fetching. */
  seasonVersion: number
  onToast: (message: string) => void
}

export interface QuestPicksController {
  questPicks: Partial<Record<QuestId, QuestPickState>>
  /** This gameweek's graded quest truth. A quest absent from the map has not been
   *  graded yet — its card stays sealed rather than showing a verdict. */
  questResults: Partial<Record<QuestId, QuestResult>>
  pickTeam: (questId: QuestId, team: string) => void
  pickMatch: (questId: QuestId, game: Game) => void
  selectSide: (questId: QuestId, side: 'over' | 'under') => void
}

/**
 * Weekly quest picks for the current gameweek only.
 *
 * Unlike predictions and the Banker — which every gameweek-change call site sets together with
 * the new gameweek number — quest picks reload independently when the gameweek changes. That
 * leaves a window where `currentGameweek` has advanced but `questPicks` still holds the previous
 * gameweek's value, so a ref records which gameweek the loaded picks belong to and the autosave
 * refuses to write until the two agree. Making that guard an implementation detail of this hook,
 * rather than a rule App has to remember, is the point of the extraction.
 *
 * There is no separate confirm step: the picker sets the pick directly and the quest locks when
 * the shared gameweek deadline passes (see GameweekGrid's phase computation).
 */
export function useQuestPicks({
  currentGameweek,
  isLoading,
  seasonVersion,
  onToast,
}: UseQuestPicksOptions): QuestPicksController {
  const [questPicks, setQuestPicks] = useState<Partial<Record<QuestId, QuestPickState>>>({})
  const [questResults, setQuestResults] = useState<Partial<Record<QuestId, QuestResult>>>({})
  const loadedGameweekRef = useRef<number | null>(null)

  // Results are app-wide truth rather than this user's data, so they are loaded on their own —
  // and re-loaded on every `seasonVersion` bump, because a quest becomes gradeable when the
  // round's last fixture finishes, long after the picks were fetched. An empty map is the
  // legitimate not-yet-graded answer, which is also what a failed load falls back to.
  useEffect(() => {
    if (isLoading) return
    let cancelled = false
    loadQuestResults(currentGameweek)
      .then((results) => {
        if (!cancelled) setQuestResults(results)
      })
      .catch(() => {
        if (!cancelled) setQuestResults({})
      })
    return () => {
      cancelled = true
    }
  }, [currentGameweek, isLoading, seasonVersion])

  useEffect(() => {
    if (isLoading) return
    let cancelled = false
    const gameweek = currentGameweek
    loadQuestPicks(gameweek)
      .then((picks) => {
        if (cancelled) return
        loadedGameweekRef.current = gameweek
        setQuestPicks(picks)
      })
      .catch(() => {
        // A failed load reads as "no picks yet" rather than blocking the screen.
        if (cancelled) return
        loadedGameweekRef.current = gameweek
        setQuestPicks({})
      })
    return () => {
      cancelled = true
    }
  }, [currentGameweek, isLoading])

  useAutosave({
    value: questPicks,
    scope: currentGameweek,
    enabled: !isLoading,
    guard: () => loadedGameweekRef.current === currentGameweek,
    save: (value) => saveQuestPicks(currentGameweek, value),
    onError: () => onToast('Failed to save your quest picks.'),
  })

  return {
    questPicks,
    questResults,
    pickTeam: (questId, team) => setQuestPicks((prev) => ({ ...prev, [questId]: { team } })),
    pickMatch: (questId, game) =>
      setQuestPicks((prev) => ({ ...prev, [questId]: { gameId: game.id } })),
    selectSide: (questId, side) => setQuestPicks((prev) => ({ ...prev, [questId]: { side } })),
  }
}

export interface HasStartedGwController {
  hasStartedGw: boolean
  /** Called when the "Predict Now" hero is tapped; never reset to false. */
  markStarted: () => void
}

/**
 * Whether the user has started this gameweek — flipped true
 * the moment the "Predict Now" hero is tapped, and never back. It is what keeps the dimmed Weekly
 * Quests cards visible after backing out of the picker with zero picks made.
 *
 * Same reload-on-gameweek-change / ref-guard pattern as `useQuestPicks` above, for the same
 * reason, rather than an explicit reset at every gameweek-change call site. A `false` is nothing
 * to persist, so only the flip to true is written.
 */
export function useHasStartedGw(currentGameweek: number, isLoading: boolean): HasStartedGwController {
  const [hasStartedGw, setHasStartedGw] = useState(false)
  const loadedGameweekRef = useRef<number | null>(null)

  useEffect(() => {
    if (isLoading) return
    let cancelled = false
    const gameweek = currentGameweek
    loadHasStartedGw(gameweek)
      .then((started) => {
        if (cancelled) return
        loadedGameweekRef.current = gameweek
        setHasStartedGw(started)
      })
      .catch(() => {
        if (cancelled) return
        loadedGameweekRef.current = gameweek
        setHasStartedGw(false)
      })
    return () => {
      cancelled = true
    }
  }, [currentGameweek, isLoading])

  useAutosave({
    value: hasStartedGw,
    scope: currentGameweek,
    enabled: !isLoading && hasStartedGw,
    guard: () => loadedGameweekRef.current === currentGameweek,
    save: () => saveHasStartedGw(currentGameweek),
    // Non-fatal — worst case a reload re-shows the hero instead of the dimmed quest cards.
  })

  return { hasStartedGw, markStarted: () => setHasStartedGw(true) }
}
